import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { formatChartTitle } from '../../utils';

interface DonutChartData {
  label: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  title?: string;
  height?: number;
}

const CHART_COLORS = ['#4DD0E1', '#FF9800', '#1976D2', '#4CAF50', '#F44336'];

const SMALL_SEGMENT_THRESHOLD = 10;

// --- Label types ---

interface InternalLabel {
  type: 'internal';
  percentage: string;
  x: number;
  y: number;
}

interface ExternalLabel {
  type: 'external';
  percentage: string;
  // Point on outer arc edge (line starts here)
  arcX: number;
  arcY: number;
  // Elbow point (end of radial segment, start of horizontal segment)
  elbowX: number;
  elbowY: number;
  // Text position (end of horizontal segment)
  textX: number;
  textY: number;
  textAnchor: 'start' | 'end';
  side: 'left' | 'right';
}

type LabelPosition = InternalLabel | ExternalLabel;

// --- Geometry helpers ---

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

// MUI X Charts with startAngle=0: starts at top (12 o'clock), goes clockwise.
// In standard math: top = 90°, clockwise = decrementing angle.
// So: mathAngle = 90 - muiAngle
// Position: x = cx + r * cos(mathAngle), y = cy - r * sin(mathAngle)

function computeLabelPositions(
  chartData: { percentage: number }[],
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  containerWidth: number,
  formatPercentage: (v: number) => string,
): LabelPosition[] {
  const radialLength = 12;
  const horizontalLength = 16;
  let muiAngle = 0; // MUI cumulative angle (starts at 0 = top, increases clockwise)

  const positions: LabelPosition[] = [];

  for (const item of chartData) {
    const sliceAngle = (item.percentage / 100) * 360;
    const midMuiAngle = muiAngle + sliceAngle / 2;
    // Convert to standard math angle
    const mathAngle = 90 - midMuiAngle;
    const rad = degToRad(mathAngle);

    const cosA = Math.cos(rad);
    const sinA = Math.sin(rad);

    if (item.percentage < 0.5) {
      // Skip labels for segments too small to be visible
      muiAngle += sliceAngle;
      continue;
    }

    if (item.percentage >= SMALL_SEGMENT_THRESHOLD) {
      // Large segment: label inside the donut band
      const midR = (innerRadius + outerRadius) / 2;
      positions.push({
        type: 'internal',
        percentage: formatPercentage(item.percentage),
        x: cx + midR * cosA,
        y: cy - midR * sinA,
      });
    } else {
      // Small segment: elbow leader line
      const arcX = cx + outerRadius * cosA;
      const arcY = cy - outerRadius * sinA;

      const elbowR = outerRadius + radialLength;
      const elbowX = cx + elbowR * cosA;
      const elbowY = cy - elbowR * sinA;

      // Horizontal goes left or right based on which side of the chart
      const isRight = cosA >= 0;
      const textX = elbowX + (isRight ? horizontalLength : -horizontalLength);

      positions.push({
        type: 'external',
        percentage: formatPercentage(item.percentage),
        arcX,
        arcY,
        elbowX,
        elbowY,
        textX,
        textY: elbowY,
        textAnchor: isRight ? 'start' : 'end',
        side: isRight ? 'right' : 'left',
      });
    }

    muiAngle += sliceAngle;
  }

  return resolveExternalCollisions(positions, containerWidth);
}

function resolveExternalCollisions(positions: LabelPosition[], containerWidth: number): LabelPosition[] {
  const minGap = 16;
  const externals = positions.filter((p): p is ExternalLabel => p.type === 'external');

  const rightLabels = externals.filter((p) => p.side === 'right');
  const leftLabels = externals.filter((p) => p.side === 'left');

  for (const group of [rightLabels, leftLabels]) {
    // Sort by Y position (top to bottom)
    group.sort((a, b) => a.textY - b.textY);
    for (let i = 1; i < group.length; i++) {
      const prev = group[i - 1];
      const curr = group[i];
      if (curr.textY - prev.textY < minGap) {
        curr.textY = prev.textY + minGap;
        curr.elbowY = curr.textY;
      }
    }
  }

  // Clamp textX to stay within container bounds
  for (const ext of externals) {
    if (ext.side === 'right') {
      ext.textX = Math.min(ext.textX, containerWidth - 8);
    } else {
      ext.textX = Math.max(ext.textX, 8);
    }
  }

  return positions;
}

export const DonutChart: React.FC<DonutChartProps> = ({ data, title, height = 300 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const theme = useTheme();
  const formattedTitle = formatChartTitle(title);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const total = useMemo(
    () => data.reduce((sum, item) => sum + (Number.isFinite(item.value) ? item.value : 0), 0),
    [data],
  );

  const formatPercentage = (value: number) => {
    if (!Number.isFinite(value)) return '0%';
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
  };

  const chartData = data.map((item, index) => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    return {
      id: index,
      value: item.value,
      label: item.label,
      percentage,
    };
  });

  const colors = data.map((item, index) => item.color || CHART_COLORS[index % CHART_COLORS.length]);

  const innerRadius = 60;
  const outerRadius = 100;
  const chartHeight = height;
  const topMargin = 20;
  const cx = containerWidth / 2;
  const cy = topMargin + (chartHeight - topMargin) / 2;

  const labelPositions = useMemo(
    () =>
      containerWidth > 0
        ? computeLabelPositions(chartData, cx, cy, innerRadius, outerRadius, containerWidth, formatPercentage)
        : [],
    [chartData, cx, cy, containerWidth],
  );

  return (
    <Box>
      {formattedTitle && (
        <Typography variant='h6' fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <Box ref={containerRef} sx={{ width: '100%', position: 'relative', overflow: 'visible' }}>
        <PieChart
          series={[
            {
              data: chartData,
              startAngle: 0,
              innerRadius,
              outerRadius,
              paddingAngle: 2,
              valueFormatter: (item) => formatPercentage((item as { percentage?: number }).percentage ?? 0),
              highlightScope: { fade: 'global', highlight: 'item' },
            },
          ]}
          colors={colors}
          height={chartHeight}
          slotProps={{
            legend: {
              direction: 'horizontal',
              position: { vertical: 'bottom', horizontal: 'center' },
            },
          }}
          margin={{ top: topMargin, right: 0, bottom: 0, left: 0 }}
        />
        {containerWidth > 0 && (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: containerWidth,
              height: chartHeight,
              pointerEvents: 'none',
              overflow: 'visible',
            }}
          >
            {labelPositions.map((pos, i) =>
              pos.type === 'internal' ? (
                <text
                  key={i}
                  x={pos.x}
                  y={pos.y}
                  textAnchor='middle'
                  dominantBaseline='central'
                  fill={theme.palette.text.primary}
                  fontSize={12}
                  fontWeight={600}
                  fontFamily={theme.typography.fontFamily}
                >
                  {pos.percentage}
                </text>
              ) : (
                <g key={i}>
                  <polyline
                    points={`${pos.arcX},${pos.arcY} ${pos.elbowX},${pos.elbowY} ${pos.textX},${pos.textY}`}
                    fill='none'
                    stroke={theme.palette.text.secondary}
                    strokeWidth={1}
                  />
                  <text
                    x={pos.textX}
                    y={pos.textY}
                    textAnchor={pos.textAnchor}
                    dominantBaseline='central'
                    fill={theme.palette.text.primary}
                    fontSize={11}
                    fontWeight={600}
                    fontFamily={theme.typography.fontFamily}
                  >
                    {pos.percentage}
                  </text>
                </g>
              ),
            )}
          </svg>
        )}
      </Box>
    </Box>
  );
};
